import { CampfireRichEditor } from '@campfire/campfire-rich-editor';
import { TabletButton } from '@campfire/tablet-button';
import { Box, Switch, Typography } from '@material-ui/core';
import { HourglassEmpty } from '@material-ui/icons';
import { convertFromRaw, convertToRaw, EditorState } from 'draft-js';
import React, { useEffect, useMemo, useState } from 'react';
import { useSnackbar } from '../../../../../global/config/useSnackbar';
import { useApiUrl } from '../../../../../global/config/useApiUrl';
import { useCampfireFetch } from '../../../../../global/network/useCampfireFetch';
import { useCampfireQuery } from '../../../../../global/network/useCampfireQuery';
import { GET_PROFILE_RECEIVED_CONTENT } from './admin-console-applicant-screening.gql';

interface ProfileReceivedContentData {
  vm: {
    profileReceivedContent: {
      profileReceivedContentId: string;
      content: string | null;
      isEnabled: boolean;
    } | null;
  };
}

export const ProfileReceivedEditForm = () => {
  const { setSnackbar } = useSnackbar();
  const apiUrl = useApiUrl();
  const { run } = useCampfireFetch({ defer: true });

  const { data, loading } = useCampfireQuery<ProfileReceivedContentData, undefined>(GET_PROFILE_RECEIVED_CONTENT);

  const [isEnabled, setIsEnabled] = useState(false);
  const [editorState, setEditorState] = useState(() => EditorState.createEmpty());
  const [isSaving, setIsSaving] = useState(false);

  const existingContent = useMemo(() => data?.vm.profileReceivedContent, [data]);

  useEffect(() => {
    if (!existingContent) return;
    setIsEnabled(existingContent.isEnabled);
    if (existingContent.content) {
      try {
        setEditorState(EditorState.createWithContent(convertFromRaw(JSON.parse(existingContent.content))));
      } catch {
        setEditorState(EditorState.createEmpty());
      }
    }
  }, [existingContent]);

  const handleSave = () => {
    const contentRaw = editorState.getCurrentContent().hasText()
      ? JSON.stringify(convertToRaw(editorState.getCurrentContent()))
      : null;

    setIsSaving(true);
    run({
      url: `${apiUrl}/vm/application/profile-received-content/save`,
      method: 'post',
      data: {
        content: contentRaw,
        isEnabled,
      },
    })
      .then((res) => {
        setSnackbar({
          open: true,
          message: res.ok ? 'Profile under review message saved' : 'Failed to save',
          variant: res.ok ? 'success' : 'error',
        });
      })
      .catch(() => {
        setSnackbar({ open: true, message: 'Network error', variant: 'error' });
      })
      .finally(() => setIsSaving(false));
  };

  if (loading) return null;

  return (
    <Box>
      <Typography style={{ fontWeight: 600, fontSize: '16px', display: 'flex', alignItems: 'center' }}>
        <HourglassEmpty style={{ fontSize: '14px', paddingRight: '5px' }} /> Profile Under Review Message
      </Typography>
      <Typography variant='body2' style={{ fontSize: '14px', color: '#808080' }}>
        Customise the message applicants see while their completed profile is awaiting review
      </Typography>

      <Box display='flex' flex='1 1 auto' marginTop='1.5rem'>
        <Box style={{ paddingTop: 4, paddingRight: 8 }}>
          <Switch color='primary' checked={isEnabled} onChange={(e) => setIsEnabled(e.target.checked)} />
        </Box>
        <Box>
          <Box ml={1}>
            <Typography style={{ fontWeight: 500, fontSize: '14px', color: '#333' }}>
              {`Enable Custom Profile Under Review Message (${isEnabled ? 'On' : 'Off'})`}
            </Typography>
            <Typography style={{ fontSize: '12px', color: '#808080' }}>
              {isEnabled
                ? 'Applicants will see this message after completing their profile, until a manager reviews it.'
                : 'Applicants will see the default message saying their profile is awaiting review.'}
            </Typography>
          </Box>
        </Box>
      </Box>

      {isEnabled && (
        <Box marginTop='1.5rem'>
          <Typography style={{ fontWeight: 500, fontSize: '14px', color: '#333', marginBottom: 8 }}>Message</Typography>
          <Box border='1px solid #ddd' borderRadius={4} padding='8px 12px' minHeight={120}>
            <CampfireRichEditor
              placeholder='Enter the message shown to applicants while their profile is awaiting review...'
              editorState={editorState}
              setEditorState={setEditorState}
            />
          </Box>
          <Typography style={{ fontSize: '12px', color: '#808080', marginTop: 4 }}>
            Leave blank to show the default message.
          </Typography>
        </Box>
      )}

      <Box marginTop='1rem' display='flex' justifyContent='flex-end'>
        <TabletButton variant='contained' color='primary' onClick={handleSave} disabled={isSaving}>
          {isSaving ? 'Saving...' : 'Save'}
        </TabletButton>
      </Box>
    </Box>
  );
};
